import {
  BifrostChunk,
  MAX_CHUNKS_PER_TRANSFER,
  RpcEnvelope,
  RpcResponse,
} from "../types/protocol";

export interface ChunkConfig {
  chunkThreshold?: number;
  chunkSize?: number;
  maxTransferSize?: number;
}

export interface ChunkSenderConfig extends ChunkConfig {
  send: (message: any, serialized?: string) => Promise<void> | void;
}

interface SendSession {
  chunkThreshold: number;
  send: (message: any, serialized?: string) => Promise<void> | void;
}

function utf8Scalar(
  value: string,
  index: number,
): {
  bytes: number;
  codeUnits: number;
} {
  const code = value.charCodeAt(index);
  if (
    code >= 0xd800 &&
    code <= 0xdbff &&
    index + 1 < value.length &&
    value.charCodeAt(index + 1) >= 0xdc00 &&
    value.charCodeAt(index + 1) <= 0xdfff
  ) {
    return { bytes: 4, codeUnits: 2 };
  }
  return {
    bytes: code <= 0x7f ? 1 : code <= 0x7ff ? 2 : 3,
    codeUnits: 1,
  };
}

/** UTF-8 size without allocating an encoded copy of a potentially large message. */
export function utf8ByteLength(value: string): number {
  let bytes = 0;
  for (let index = 0; index < value.length; ) {
    const scalar = utf8Scalar(value, index);
    bytes += scalar.bytes;
    index += scalar.codeUnits;
  }
  return bytes;
}

/** Maximum chunks a greedy scalar-safe UTF-8 split can require. */
export function maxUtf8ChunksForByteLength(
  byteLength: number,
  chunkSize: number,
): number {
  if (byteLength <= 0) return 0;
  if (chunkSize < 4) return Infinity;

  const minimumBytes = (chunks: number): number => {
    if (chunks <= 1) return 1;
    if (chunkSize <= 7) {
      return (
        Math.ceil(chunks / 2) * (chunkSize - 3) + Math.floor(chunks / 2) * 4
      );
    }
    return (chunks - 1) * (chunkSize - 3) + 4;
  };

  let low = 1;
  let high = Math.ceil(byteLength / (chunkSize - 3)) + 1;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (minimumBytes(middle) <= byteLength) low = middle;
    else high = middle - 1;
  }
  return low;
}

interface PreparedMessage {
  serialized: string;
  sizeBytes: number;
}

const preparedMessages = new WeakMap<object, PreparedMessage>();

/** Cache the bridge's deadline measurement for the immediately following send. */
export function prepareMessage(message: RpcEnvelope | RpcResponse): number {
  const serialized = JSON.stringify(message);
  const prepared = { serialized, sizeBytes: utf8ByteLength(serialized) };
  preparedMessages.set(message, prepared);
  return prepared.sizeBytes;
}

export function assertValidChunkConfig(config: ChunkConfig): void {
  const chunkSize = config.chunkSize ?? 128 * 1024;
  const maxTransferSize = config.maxTransferSize ?? 16 * 1024 * 1024;

  if (!Number.isInteger(chunkSize) || chunkSize <= 0) {
    throw new Error(`chunkSize must be a positive integer, was ${chunkSize}`);
  }
  if (!Number.isInteger(maxTransferSize) || maxTransferSize <= 0) {
    throw new Error(
      `maxTransferSize must be a positive integer, was ${maxTransferSize}`,
    );
  }
  if (
    maxUtf8ChunksForByteLength(maxTransferSize, chunkSize) >
    MAX_CHUNKS_PER_TRANSFER
  ) {
    throw new Error(
      `maxTransferSize ${maxTransferSize} requires more than ${MAX_CHUNKS_PER_TRANSFER} chunks at chunkSize ${chunkSize}`,
    );
  }
}

/** How many chunks to push before handing the thread back to the event loop. */
const CHUNKS_PER_YIELD = 16;
/** Bound per-send promise and closure overhead that the byte limit cannot see. */
const MAX_PENDING_SENDS = 100;

const yieldToEventLoop = (): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, 0));

export function splitIntoChunks(
  transferId: string,
  data: string,
  chunkSize: number,
  responseId?: string,
): BifrostChunk[] {
  return splitIntoChunksWithByteLength(
    transferId,
    data,
    chunkSize,
    utf8ByteLength(data),
    responseId,
  );
}

function splitIntoChunksWithByteLength(
  transferId: string,
  data: string,
  chunkSize: number,
  dataBytes: number,
  responseId?: string,
): BifrostChunk[] {
  if (!transferId) throw new Error("transferId must not be empty");
  if (!data) throw new Error("data must not be empty");
  if (responseId === "") throw new Error("responseId must not be empty");

  // chunkSize reaches here from setChunkConfig, so it is caller-controlled.
  // Zero made `total` Infinity and looped forever on empty slices; a negative
  // value produced zero chunks and silently discarded the message.
  if (!Number.isInteger(chunkSize) || chunkSize <= 0) {
    throw new Error(`chunkSize must be a positive integer, was ${chunkSize}`);
  }

  const pieces: string[] = [];
  const push = (piece: string): void => {
    if (pieces.length === MAX_CHUNKS_PER_TRANSFER) {
      throw new Error("TOO_MANY_CHUNKS");
    }
    pieces.push(piece);
  };

  // JSON and base64 are normally ASCII. Keep that dominant path at one slice
  // per chunk rather than scanning every code point on the WebView thread.
  if (dataBytes === data.length) {
    for (let start = 0; start < data.length; start += chunkSize) {
      push(data.slice(start, start + chunkSize));
    }
  } else {
    let start = 0;
    while (start < data.length) {
      let end = start;
      let bytes = 0;
      while (end < data.length) {
        const scalar = utf8Scalar(data, end);
        if (scalar.bytes > chunkSize) {
          throw new Error(
            `chunkSize must fit one UTF-8 character, was ${chunkSize}`,
          );
        }
        if (bytes + scalar.bytes > chunkSize) break;
        bytes += scalar.bytes;
        end += scalar.codeUnits;
      }
      push(data.slice(start, end));
      start = end;
    }
  }

  return pieces.map((piece, index) => ({
    type: "bifrost_chunk",
    transferId,
    ...(responseId ? { responseId } : {}),
    index,
    total: pieces.length,
    data: piece,
  }));
}

let transferCounter = 0;

export class ChunkSender {
  private chunkThreshold: number;
  private chunkSize: number;
  private maxTransferSize: number;
  private readonly send: (
    message: any,
    serialized?: string,
  ) => Promise<void> | void;
  private sendTail?: Promise<void>;
  private pendingBytes = 0;
  private pendingSends = 0;

  constructor(config: ChunkSenderConfig) {
    assertValidChunkConfig(config);
    this.chunkThreshold = config.chunkThreshold ?? 256 * 1024;
    this.chunkSize = config.chunkSize ?? 128 * 1024;
    this.maxTransferSize = config.maxTransferSize ?? 16 * 1024 * 1024;
    this.send = config.send;
  }

  setConfig(config: ChunkConfig): void {
    assertValidChunkConfig(config);
    this.chunkThreshold = config.chunkThreshold ?? 256 * 1024;
    this.chunkSize = config.chunkSize ?? 128 * 1024;
    this.maxTransferSize = config.maxTransferSize ?? 16 * 1024 * 1024;
  }

  async sendMessage(
    message: RpcEnvelope | RpcResponse,
    responseId?: string,
    onStart?: (chunkThreshold: number) => void,
    shouldSend?: () => boolean,
    startSend?: () => SendSession,
  ): Promise<void> {
    const prepared = preparedMessages.get(message);
    preparedMessages.delete(message);
    const serialized = prepared?.serialized ?? JSON.stringify(message);
    const sizeBytes = prepared?.sizeBytes ?? utf8ByteLength(serialized);
    const chunkSize = this.chunkSize;
    const maxTransferSize = this.maxTransferSize;

    // Checked ahead of the threshold shortcut: the cap is on the message, not on
    // the chunked path, and a chunkThreshold above maxTransferSize would
    // otherwise let an oversized message through unchunked and unchecked.
    if (sizeBytes > maxTransferSize) {
      throw new Error("MESSAGE_TOO_LARGE");
    }
    // Keep one full-size message active and one waiting, matching the
    // receiver's default aggregate headroom without retaining an unbounded
    // promise chain of serialized payloads.
    if (
      this.pendingSends >= MAX_PENDING_SENDS ||
      this.pendingBytes + sizeBytes > maxTransferSize * 2
    ) {
      throw new Error("OUTBOUND_QUEUE_FULL");
    }
    this.pendingBytes += sizeBytes;
    this.pendingSends++;

    const send = async (): Promise<void> => {
      if (shouldSend && !shouldSend()) return;
      const session = startSend?.();
      const chunkThreshold = session?.chunkThreshold ?? this.chunkThreshold;
      const sendFrame = session?.send ?? this.send;
      onStart?.(chunkThreshold);
      if (sizeBytes <= chunkThreshold) {
        await sendFrame(message, serialized);
        return;
      }

      const transferId = `tx_${++transferCounter}_${Date.now()}`;
      const chunks = splitIntoChunksWithByteLength(
        transferId,
        serialized,
        chunkSize,
        sizeBytes,
        responseId,
      );

      for (let i = 0; i < chunks.length; i++) {
        if (shouldSend && !shouldSend()) return;
        await sendFrame(chunks[i]);
        // Awaiting a synchronous send only drains microtasks, so without an
        // explicit macrotask yield a large transfer holds the thread from first
        // chunk to last: no paint, no touch handling, no timers for its whole
        // duration. Yielding periodically rather than every chunk keeps the cost
        // proportionate.
        if ((i + 1) % CHUNKS_PER_YIELD === 0 && i + 1 < chunks.length) {
          await yieldToEventLoop();
        }
      }
    };

    // Start an idle send immediately, preserving synchronous native delivery
    // for the common small-message path. Only overlapping sends queue behind
    // the active one so multipart transfers remain ordered and contiguous.
    let result: Promise<void>;
    let tail: Promise<void> | undefined;
    if (this.sendTail) {
      result = this.sendTail.then(send);
      tail = result.catch(() => {});
      this.sendTail = tail;
    } else {
      let release!: () => void;
      const active = new Promise<void>((resolve) => {
        release = resolve;
      });
      this.sendTail = active;
      result = send();
      void result.then(release, release);

      // A synchronous native callback may already have queued work behind the
      // active marker. Do not overwrite that newer tail.
      if (this.sendTail === active) {
        tail = result.catch(() => {});
        this.sendTail = tail;
      }
    }
    try {
      await result;
    } finally {
      this.pendingBytes -= sizeBytes;
      this.pendingSends--;
      if (tail && this.sendTail === tail) this.sendTail = undefined;
    }
  }
}
